import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BreadcrumbItem } from "../DashboardNew/BreadcrumbHeader";
import { useOrganizationService, useResourcesService } from "../../services";
import useAzureService from "../../services/azure";
import { useJwt } from "../../helpers/common";
import useIntegrationStatus from "../../hooks/useIntegrationStatus";
import { SearchMode, TabType, ViewType } from "../Resource/types";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { ComponentPage } from "../../constants/constants";
import {
  CreateTeamModal,
  ImportTeamsDrawer,
  ImportUsersDrawer,
  PeopleContent,
  PeopleContentSkeleton,
  TabBar,
  TeamsContent,
  TeamsContentSkeleton
} from "./components";
import { ResourceHeader } from "../Resource/components";
import { useSideNavContext } from "../../context/SideNavContext";
import InviteTeamModal from "./components/InviteTeamModal";
import {
  setResourceActiveTab,
  setResourceSearchMode,
  setResourceSearchTerm,
  setResourcesSprints,
  setTeamSprints
} from "../../redux/slices/resources";
import { Sprint } from "../../services/organization";
import { InvitationGuide } from "../../features/OnboardingGuide/InvitationGuide/InvitationGuide";

const ManageResources: React.FC = () => {
  const dispatch = useDispatch();
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const user_id = JWTdata.user_id;
  const location = useLocation();

  // Determine current tab from URL path
  const isTeamsPath = location.pathname.includes('/teams');
  const activeTab = isTeamsPath ? TabType.TEAMS : TabType.PEOPLE;

  useEffect(() => {
    dispatch(setResourceActiveTab(activeTab));
  }, [activeTab, dispatch]);

  const { isOpenOnSmallScreens } = useSideNavContext();
  const { fetchSprints, fetchOrganizationSkill } = useOrganizationService();
  const { fetchManagerResources } = useResourcesService();
  const { fetchTeams } = useOrganizationService();
  const { fetchAzureTeamSyncDifferences } = useAzureService();
  const { isIntegrationActive, activeIntegrations } = useIntegrationStatus();
  const azureActive = isIntegrationActive('azure');

  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showInviteTeamModal, setShowInviteTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [showImportTeams, setShowImportTeams] = useState(false);
  const [showImportUsers, setShowImportUsers] = useState(false);
  const [showOnlyTeamsWithChanges, setShowOnlyTeamsWithChanges] = useState(false);

  // We don't link these to ComponentPage so they don't get saved in Saved Views
  const { filters: urlFilters, setFilter } = useUrlFilters([
    { urlKey: 'id', storageKey: 'res_manage_id', defaultValue: '' }
  ]);

  const {
    data: resources,
    resourcePageState,
    manageResourcesLoading: isResourcesLoading,
    manageResourcesError: resourcesError,
    resourcePageSprints,
  } = useSelector((state: any) => state.resources);

  const { searchTerm, searchMode, selectedTeamId: storedSelectedTeamId } = resourcePageState;

  const { teams: organizationTeams, teamsLoading } = useSelector((state: any) => state.organization);

  const fetchAllSprints = async () => {
    if (!azureActive) {
      return;
    }
    const sprints = await fetchSprints('azure');
    dispatch(setResourcesSprints(sprints?.filter(Boolean) || []));
  }

  useEffect(() => {
    if (org_id) {
      fetchManagerResources(org_id);
      fetchOrganizationSkill(org_id);
      fetchTeams(org_id);
    }
  }, [org_id]);

  useEffect(() => {
    fetchAllSprints()
  }, [activeIntegrations])

  useEffect(() => {
    dispatch(setResourceSearchTerm(''))
  }, [searchMode]);

  // Azure sync with IndexedDB caching
  useEffect(() => {
    if (org_id && user_id && isIntegrationActive("azure")) {
      fetchAzureTeamSyncDifferences(org_id, user_id);
    }
  }, [org_id, user_id, activeIntegrations.length]);

  // Sync Team Modal URL parameter to local state for deep linking
  useEffect(() => {
    if (isTeamsPath) {
      if (urlFilters.id) {
        if (urlFilters.id === 'new') {
          // 'new' signifies the Create Team modal
          setEditingTeam(null);
          setShowCreateTeamModal(true);
        } else if (organizationTeams && organizationTeams.length > 0) {
          // Otherwise it's an ID to edit an existing team
          const teamId = urlFilters.id as string;
          const teamToEdit = organizationTeams.find((tm: any) => String(tm.id) === teamId);
          
          if (teamToEdit) {
            setEditingTeam(teamToEdit);
            setShowCreateTeamModal(true);
          } else {
            // If team ID is invalid, clear state and URL
            setEditingTeam(null);
            setShowCreateTeamModal(false);
            setFilter('id', '');
          }
        }
      } else if (!urlFilters.id) {
        // Handle back button
        setEditingTeam(null);
        setShowCreateTeamModal(false);
      }
    }
  }, [urlFilters.id, organizationTeams, setFilter, isTeamsPath]);

  const selectedTeam = useMemo(() => {
    if (!storedSelectedTeamId || !organizationTeams || !Array.isArray(organizationTeams)) return null;
    return organizationTeams.find((tm: any) => tm && String(tm?.id) === String(storedSelectedTeamId)) || null;
  }, [storedSelectedTeamId, organizationTeams])

  useEffect(() => {
    // Early return if no team selected or teams not loaded
    if (!selectedTeam || !Array.isArray(resourcePageSprints)) {
      if (selectedTeam === null) {
        dispatch(setTeamSprints([]));
      }
      return;
    }

    const teamUUID = selectedTeam?.team_id || '';
    const projectId = selectedTeam?.projects?.[0]?.project_id ? String(selectedTeam.projects[0].project_id) : '';

    if (!teamUUID) {
      dispatch(setTeamSprints([]));
      return;
    }

    let filtered = resourcePageSprints.filter((s: Sprint) => {
      if (!s || !s.team_id) return false;
      return String(s.team_id) === String(teamUUID);
    });

    if (projectId) {
      filtered = filtered.filter((s: Sprint) => {
        if (!s || !s.project_id) return false;
        return String(s.project_id) === String(projectId);
      });
    }

    dispatch(setTeamSprints(filtered));
  }, [selectedTeam, resourcePageSprints]);

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    return [
      { label: 'Capacity Planning', href: '#/resources', active: false },
      { label: 'Manage Resources', href: '#', active: true }
    ];
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      navigate('/resources');
    }
  };

  const handleManageInvitesClick = () => {
    setShowInviteTeamModal(true);
  };

  const handleCloseInviteTeamModal = () => {
    setShowInviteTeamModal(false);
  };

  const handleCreateTeamClick = () => {
    setFilter('id', 'new');
  };

  const handleImportTeamsClick = () => setShowImportTeams(true);
  const handleImportUsersClick = () => setShowImportUsers(true);

  const handleCloseCreateTeamModal = () => {
    setFilter('id', '');
    setShowCreateTeamModal(false);
    setEditingTeam(null);
  };

  const handleEditTeam = (team: any) => {
    setFilter('id', String(team.id));
  };

  const handleTeamsWithChangesClick = () => {
    navigate('/teams');
    setShowOnlyTeamsWithChanges(true);
  };

  const refreshTeamsData = async () => {
    if (org_id) {
      await fetchTeams(org_id);
      await fetchManagerResources(org_id);
    }
  };

  const breadcrumbs = getBreadcrumbs();

  const filteredResources = useMemo(() => {
    if (!resources || !searchTerm.trim()) {
      return resources || [];
    }

    const searchTermLower = searchTerm.toLowerCase();

    return resources.filter((resource: any) => {
      if (searchMode === SearchMode.NAME) {
        return resource.name?.toLowerCase().includes(searchTermLower) ||
          resource.email?.toLowerCase().includes(searchTermLower);
      } else if (searchMode === SearchMode.SKILLS) {
        if (!resource.skills_name || resource.skills_name.length === 0) return false;
        return resource.skills_name.some((skillName: string) =>
          skillName?.toLowerCase().includes(searchTermLower)
        );
      } else if (searchMode === SearchMode.TEAM) {
        if (!resource.teams || resource.teams.length === 0) return false;
        return resource.teams.some((team: any) =>
          team.name?.toLowerCase().includes(searchTermLower)
        );
      }
      return false;
    });
  }, [resources, searchTerm, searchMode]);

  const navigate = useNavigate();

  const handleTabChange = (tab: TabType) => {
    navigate(tab === TabType.TEAMS ? '/teams' : '/people');
    dispatch(setResourceActiveTab(tab));
    dispatch(setResourceSearchTerm(''));
    dispatch(setResourceSearchMode(SearchMode.NAME));
  };

  return (
    <>
      <InvitationGuide handleManageInvitesClick={handleManageInvitesClick}/>
      <div
        className="flex flex-col h-screen"
        style={{
          width: !isOpenOnSmallScreens
            ? "calc(100vw - 255px)"
            : "calc(100vw - 65px)",
        }}
      >
        <div className="relative z-50">
          <ResourceHeader
            breadcrumbs={breadcrumbs}
            onBreadcrumbClick={handleBreadcrumbClick}
            onManageInvitesClick={handleManageInvitesClick}
            onCreateTeamClick={handleCreateTeamClick}
            onImportTeamsClick={handleImportTeamsClick}
            onImportUsersClick={handleImportUsersClick}
            currentView={ViewType.PEOPLE_TEAMS}
            onTeamsWithChangesClick={handleTeamsWithChangesClick}
          />
        </div>

        <TabBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          searchTerm={searchTerm}
          searchMode={searchMode}
          onSearchTermChange={(term: string) =>
            dispatch(setResourceSearchTerm(term))
          }
          onSearchModeChange={(mode: SearchMode) =>
            dispatch(setResourceSearchMode(mode))
          }
          handleBackClick={() => navigate('/resources')}
        />

        <div className="flex-1 overflow-auto bg-tertiary-light dark:bg-tertiary-dark">
          <div className="p-6 h-full w-full">
            {activeTab === TabType.PEOPLE && (
              <>
                {isResourcesLoading ? (
                  <PeopleContentSkeleton />
                ) : (
                  <PeopleContent
                    resources={filteredResources}
                    searchTerm={searchTerm}
                    error={resourcesError}
                  />
                )}
              </>
            )}
            {activeTab === TabType.TEAMS && (
              <>
                {teamsLoading ? (
                  <TeamsContentSkeleton />
                ) : (
                  <TeamsContent
                    searchTerm={searchTerm}
                    searchMode={searchMode}
                    onEditTeam={handleEditTeam}
                    showOnlyTeamsWithChanges={showOnlyTeamsWithChanges}
                    onToggleTeamsWithChanges={(show: boolean) =>
                      setShowOnlyTeamsWithChanges(show)
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>

        <CreateTeamModal
          isVisible={showCreateTeamModal}
          onClose={handleCloseCreateTeamModal}
          editingTeam={editingTeam}
          onRefresh={refreshTeamsData}
          onImportUsersClick={handleImportUsersClick}
        />

        <InviteTeamModal
          isVisible={showInviteTeamModal}
          onClose={handleCloseInviteTeamModal}
        />

        <ImportTeamsDrawer
          isVisible={showImportTeams}
          onClose={() => setShowImportTeams(false)}
        />
        <ImportUsersDrawer
          isVisible={showImportUsers}
          onClose={() => setShowImportUsers(false)}
        />
      </div>
    </>
  );
};

export default ManageResources;
